import { LitElement, css, html, nothing } from "lit";
import { CHAT_COLUMN_MAX_WIDTH } from "../chat-column-metrics.js";
import type { PropertyValues } from "lit";
import { customElement, property, state as litState } from "lit/decorators.js";
import {
  MAX_ATTACHMENT_COUNT,
  SUPPORTED_ATTACHMENT_TYPES,
  admitNewAttachments,
  isSupportedAttachmentFile,
  toAfasFile,
  type AfasAttachmentFile,
} from "../attachment-file.js";
import { unescapeMarkdown } from "../markdown-escape.js";
import {
  countMessageCharacters,
  formatMessageTooLong,
  isMessageTooLong,
  useMessageCharacterCount,
} from "../message-length.js";
import { DEFAULT_MAX_MESSAGE_LENGTH } from "../config.js";
import { requestImageDialog } from "./jonas-image-dialog.js";
import "./jonas-suggestions.js";
import type { ChatSuggestion } from "./jonas-suggestions.js";
import type { ChatAttachmentInput, ChatLabels } from "../types.js";

type CommentFormApi = HTMLElement & {
  init?: (config: { id: string }) => Promise<unknown> | unknown;
  reset?: (focus?: boolean) => void;
  setContent?: (content: string) => void;
  getContent?: () => string;
  characterLimit?: number;
};

const COMMENT_FORM_ID = "help-jonas-chat-composer-input";
const SUPPORTED_ATTACHMENT_ACCEPT = [...SUPPORTED_ATTACHMENT_TYPES].join(",");
// How long the "blocked" cue stays after a pick, paste or drop lost a file (#110, #385).
const ATTACHMENT_REJECTED_HINT_MS = 4000;
// Covers the suggestion exit animation plus its per-row stagger.
const SUGGESTION_EXIT_MS = 340;

/**
 * Everything Help changes inside `afas-comment-form`, in one place so the whole set can be
 * dropped when the component offers a supported lever for it. See
 * `applyCommentFormOverrides`.
 *
 * In plain mode there is nothing to format, but the form keeps rendering the button that
 * opens the formatting menu: renderInlineToolbar() only looks at `toolbar`, never at
 * whether a menubar exists. `toolbar="none"` is no way out -- the whole action row hangs
 * off it, including our own attachment button and the send button. Only direct children
 * are hit: the attachment button is slotted and the send button sits in a div.
 */
const COMMENT_FORM_OVERRIDES = `
  .compact-actions > afas-button { display: none; }
`;

/** Comment-form shadow roots that already carry the sheet above, so it is injected once. */
const commentFormOverrideRoots = new WeakSet<ShadowRoot>();

@customElement("help-jonas-chat-composer")
export class HelpJonasChatComposer extends LitElement {
  static override styles = css`
    :host {
      display: block;
      padding: 16px 24px 20px;
      transition:
        transform 1000ms ease,
        padding 280ms ease;
    }

    :host([initial]) {
      padding-top: 0;
      transform: translateY(0);
    }

    @media (prefers-reduced-motion: reduce) {
      :host {
        transition: none;
      }
    }

    .composer-shell {
      position: relative;
    }

    .composer-inner {
      display: grid;
      position: relative;
      gap: 8px;
      max-width: ${CHAT_COLUMN_MAX_WIDTH}px;
      margin: 0 auto;
    }

    /*
     * Drag-and-drop attachment drop zone (#110). Shown while a file is dragged
     * over the composer (valid = accepts an image, blocked = wrong type / no
     * free slots) and briefly after a rejected drop (too large / too many). It
     * overlays the composer but stays pointer-events:none so the drag events
     * keep reaching the composer subtree that the window listeners observe.
     */
    .composer-dropzone {
      position: absolute;
      inset: 0;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 12px;
      border-radius: 8px;
      border: 2px dashed var(--color-primary-600);
      background: var(--color-background-800);
      color: var(--color-primary-600);
      font-weight: 600;
      text-align: center;
    }

    .composer-dropzone afas-icon {
      --icon-size: 28px;
      --icon-color: currentColor;
    }

    .composer-dropzone--blocked {
      border-color: var(--color-negative-600);
      background: var(--color-negative-100);
      color: var(--color-negative-600);
    }

    /*
     * The notices above the input (the standing privacy warning, the too-long warning)
     * are about what you are about to type, so they belong to the composer and ride
     * along when it drops from the landing screen to the bottom.
     *
     * They use afas-alert's small display style (5.x): a 32px row with a 16px icon, the
     * input's corner radius and a 1px border all round instead of the accent stripe.
     */
    .composer-notice {
      order: 0;
    }

    afas-comment-form {
      order: 2;
    }

    .attachment-list {
      order: 3;
    }

    .composer-locked-hint {
      order: 4;
    }

    .composer-suggestions {
      order: 5;
      /* Puts the arrow itself on the placeholder's text line. */
      padding-left: 8px;
    }

    /*
     * On the way out the prompts leave the layout at once, anchored one grid gap below
     * the composer where they already sat. The composer therefore reaches its final
     * height in the same frame it starts sliding down, so dropping them when the
     * animation ends can no longer jolt it.
     */
    .composer-suggestions--exiting {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      right: 0;
    }

    afas-comment-form {
      width: 100%;
    }

    /*
     * In the locked (fallback / outage) state the composer is non-interactive:
     * the AFAS comment form does not render a strong disabled appearance on its
     * own, so we dim it and block pointer interaction at the host level. This
     * is the "disabled" counterpart to the active composer and works in both
     * the light and dark theme (opacity is theme-agnostic). The locked hint
     * below stays fully legible so the reason remains clear.
     */
    :host([locked]) afas-comment-form {
      opacity: 0.55;
      pointer-events: none;
    }

    .composer-form-actions {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .composer-locked-hint {
      max-width: ${CHAT_COLUMN_MAX_WIDTH}px;
      margin: 4px auto 0;
      color: var(--color-foreground-700);
    }

    .attachment-list {
      max-width: 100%;
      min-width: 0;
    }

    .attachment-list afas-file-group {
      max-width: 100%;
    }

    .attachment-list afas-file {
      width: min(100%, 256px);
    }
  `;

  @property({ type: Boolean })
  accessor busy = false;

  @property({ type: Boolean })
  accessor showStop = false;

  @property({ type: Boolean, reflect: true })
  accessor locked = false;

  @property()
  accessor lockedHint = "";

  @property({ type: Boolean, reflect: true })
  accessor initial = false;

  @property({ type: Number })
  accessor focusRequest = 0;

  @property()
  accessor submitLabel = "Verstuur";

  @property()
  accessor stopLabel = "Stop";

  @property()
  accessor placeholder = "Stel je vraag aan Jonas";

  @property()
  accessor inputAriaLabel = "Vraag aan Jonas";

  @property({ attribute: false })
  accessor labels: ChatLabels | null = null;

  // The chat-home prompts: a question the visitor can send with one click.
  @property({ attribute: false })
  accessor suggestions: ChatSuggestion[] = [];

  @property({ type: Boolean })
  accessor showNotice = false;

  // The longest question the backend forwards whole (AiChat:MaxMessageLength). Anything
  // longer is cut off there without a word, so the composer warns and blocks at the same
  // number; the host renders it into the config and the thread passes it on.
  @property({ type: Number })
  accessor maxMessageLength = DEFAULT_MAX_MESSAGE_LENGTH;

  // Length of what is in the input right now, see handleContentChanged.
  @litState() private accessor messageLength = 0;

  @litState()
  private accessor attachments: ChatAttachmentInput[] = [];

  // `dragState` drives the drop-zone overlay while a file hovers (#110);
  // `attachmentRejected` shows its blocked form after a refused file (#385).
  @litState() private accessor dragState: "none" | "valid" | "blocked" = "none";
  @litState() private accessor attachmentRejected = false;
  private dragDepth = 0;
  private attachmentRejectedTimer: ReturnType<typeof setTimeout> | null = null;

  // The chat-home prompts, held on screen for the length of their exit animation after
  // the visitor leaves the landing screen (see startSuggestionExit).
  @litState() private accessor exitingSuggestions: ChatSuggestion[] = [];
  private suggestionExitTimer: ReturnType<typeof setTimeout> | null = null;

  private commentForm: CommentFormApi | null = null;

  protected override willUpdate(changedProperties: PropertyValues<this>): void {
    if (
      changedProperties.has("initial") &&
      changedProperties.get("initial") === true
    ) {
      const leaving =
        (changedProperties.get("suggestions") as ChatSuggestion[] | undefined) ??
        this.suggestions;
      this.startSuggestionExit(leaving);
    }
  }

  protected override updated(changedProperties: PropertyValues<this>): void {
    if (changedProperties.has("focusRequest")) this.focusCommentForm();
  }

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("paste", this.handleComposerPaste, {
      capture: true,
    });
    window.addEventListener("dragenter", this.handleDragEnter, true);
    window.addEventListener("dragover", this.handleDragOver, true);
    window.addEventListener("dragleave", this.handleDragLeave, true);
    window.addEventListener("drop", this.handleDrop, true);
  }

  override disconnectedCallback(): void {
    this.commentForm = null;
    window.removeEventListener("paste", this.handleComposerPaste, true);
    window.removeEventListener("dragenter", this.handleDragEnter, true);
    window.removeEventListener("dragover", this.handleDragOver, true);
    window.removeEventListener("dragleave", this.handleDragLeave, true);
    window.removeEventListener("drop", this.handleDrop, true);
    if (this.attachmentRejectedTimer) {
      clearTimeout(this.attachmentRejectedTimer);
      this.attachmentRejectedTimer = null;
    }
    if (this.suggestionExitTimer) {
      clearTimeout(this.suggestionExitTimer);
      this.suggestionExitTimer = null;
    }
    super.disconnectedCallback();
  }

  /**
   * Picking a prompt (or typing a question) drops the composer from the centre of the
   * landing screen to the bottom. The prompts are part of that move, so they stay in the
   * DOM long enough to slide out with it instead of vanishing the instant the answer starts.
   */
  private startSuggestionExit(leaving: ChatSuggestion[]): void {
    if (this.suggestionExitTimer) clearTimeout(this.suggestionExitTimer);
    if (
      leaving.length === 0 ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      this.exitingSuggestions = [];
      return;
    }

    this.exitingSuggestions = leaving;
    this.suggestionExitTimer = setTimeout(() => {
      this.suggestionExitTimer = null;
      this.exitingSuggestions = [];
    }, SUGGESTION_EXIT_MS);
  }

  private renderNotice() {
    if (!this.showNotice) return nothing;

    return html`<afas-alert
      class="composer-notice"
      type="warning"
      display-style="small"
      role="note"
      >${this.getLabel(
        "privacyNotice",
        "Stel hier je vragen aan Jonas, de AI-assistent van AFAS. Deel geen gevoelige of vertrouwelijke gegevens in dit gesprek.",
      )}
      <afas-button
        slot="actions"
        variant="minimal"
        size="small"
        icon-only
        label=${this.getLabel("privacyNoticeDismiss", "Melding sluiten")}
        @click=${() =>
          this.dispatchEvent(
            new CustomEvent("jonas-notice-dismiss", {
              bubbles: true,
              composed: true,
            }),
          )}
      >
        <afas-icon slot="icon" icon="CloseOutlined"></afas-icon>
      </afas-button>
    </afas-alert>`;
  }

  /**
   * Shows while the input holds more than the backend will forward, and goes again as
   * soon as the visitor has cut the question down. The send itself is refused in
   * handleAfasSubmit, so this is the explanation of why nothing happens on Enter.
   */
  private renderLengthNotice() {
    if (!this.isMessageTooLong()) return nothing;

    const text = formatMessageTooLong(
      this.getLabel(
        "messageTooLong",
        "Je bericht is te lang: {length} van maximaal {max} tekens. Kort het in om het te kunnen versturen.",
      ),
      this.messageLength,
      this.maxMessageLength,
    );
    return html`<afas-alert
      class="composer-notice composer-notice--length"
      type="warning"
      display-style="small"
      role="status"
      >${text}</afas-alert
    >`;
  }

  protected override render() {
    return html`
      <div class="composer-shell">
        ${this.renderDropzone()}
        <div class="composer-inner">
          ${this.renderNotice()} ${this.renderLengthNotice()}
          <afas-comment-form
            toolbar="compact"
            mode="plain"
            preferred-width=${CHAT_COLUMN_MAX_WIDTH}
            .characterLimit=${this.maxMessageLength}
            .busy=${this.busy}
            ?disabled=${this.locked}
            submit-button-label=${this.submitLabel}
            placeholder=${this.placeholder}
            .ariaLabel=${this.inputAriaLabel}
            @connected=${this.handleCommentFormConnected}
            @changed=${this.handleContentChanged}
            @keydown=${this.handleComposerKeydown}
            @submit=${this.handleAfasSubmit}
          >
            <div slot="actions" class="composer-form-actions">
              <afas-mini-file-input
                label=${this.getLabel(
                  "attachmentButtonLabel",
                  "Afbeelding toevoegen",
                )}
                .accept=${SUPPORTED_ATTACHMENT_ACCEPT}
                multiple
                disable-paste="true"
                preferred-width="400"
                @change=${this.handleAttachmentChange}
              ></afas-mini-file-input>
              ${this.showStop && !this.locked
                ? html`<afas-button
                    variant="default"
                    .label=${this.stopLabel}
                    @click=${this.handleStopButtonClick}
                  ></afas-button>`
                : null}
            </div>
          </afas-comment-form>
          ${this.renderAttachments()}
          ${this.locked && this.lockedHint
            ? html`<p class="composer-locked-hint" role="status">
                ${this.lockedHint}
              </p>`
            : null}
          ${this.renderSuggestions()}
        </div>
      </div>
    `;
  }

  /**
   * The chat-home prompts, under the input. They sit inside the composer so they travel
   * with it when it slides from the centred Home position down to the bottom. The
   * follow-ups after an answer are not here: those belong to the answer and are rendered
   * by the thread, underneath it.
   */
  private renderSuggestions() {
    if (this.exitingSuggestions.length) {
      return html`<help-jonas-suggestions
        class="composer-suggestions composer-suggestions--exiting"
        .suggestions=${this.exitingSuggestions}
        .exiting=${true}
      ></help-jonas-suggestions>`;
    }

    if (!this.suggestions.length) return null;

    return html`<help-jonas-suggestions
      class="composer-suggestions"
      .suggestions=${this.suggestions}
      .disabled=${this.busy || this.locked}
      listLabel=${this.getLabel("homeSuggestionsLabel", "Suggesties")}
    ></help-jonas-suggestions>`;
  }

  private renderDropzone() {
    const mode =
      this.dragState !== "none"
        ? this.dragState
        : this.attachmentRejected
          ? "blocked"
          : "none";
    if (mode === "none") return null;

    const blocked = mode === "blocked";
    return html`<div
      class=${blocked
        ? "composer-dropzone composer-dropzone--blocked"
        : "composer-dropzone"}
      role="status"
      aria-live="polite"
    >
      <afas-icon icon=${blocked ? "CancelFilled" : "UploadFilled"}></afas-icon>
      <span
        >${blocked
          ? this.getLabel(
              "attachmentDropRejected",
              "Alleen PNG- of JPG-afbeeldingen tot 5 MB, maximaal 4",
            )
          : this.getLabel(
              "attachmentDropHint",
              "Sleep de afbeelding hierheen",
            )}</span
      >
    </div>`;
  }

  /**
   * Restores Shift+Enter as "new line". Plain mode loads only a keymap that binds Enter to
   * a line break, and our own Enter-sends handler swallows that one key -- so a visitor
   * had no way left to break a line. Re-dispatching Enter on the editor itself gives
   * ProseMirror the key it does understand; it must not bubble, or the send handler on the
   * form would see it and submit instead.
   */
  private handleComposerKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "Enter" || !event.shiftKey) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const editor = this.commentForm?.querySelector?.(".ProseMirror");
    if (!editor) return;

    event.preventDefault();
    event.stopPropagation();
    editor.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "Enter",
        code: "Enter",
        keyCode: 13,
        which: 13,
        bubbles: false,
        cancelable: true,
      }),
    );
  };

  private async handleCommentFormConnected(event: CustomEvent): Promise<void> {
    const form = (event.currentTarget ?? event.target) as CommentFormApi | null;
    if (!form) return;

    this.commentForm = form;
    useMessageCharacterCount(form);
    if (!form.id) form.id = COMMENT_FORM_ID;
    await Promise.resolve(form.init?.({ id: form.id }));
    this.applyCommentFormOverrides(form);
    this.focusCommentForm();
  }

  /**
   * Bends `afas-comment-form` into the plain composer: the formatting button it keeps
   * rendering has no property, slot or part, so a stylesheet in its shadow root is the
   * only lever -- the same approach the conversations panel takes with
   * `afas-chat-overview`. The whole set lives in COMMENT_FORM_OVERRIDES, so a component
   * version that supports this can drop the constant and this method with it.
   */
  private applyCommentFormOverrides(form: CommentFormApi): void {
    if (!form.shadowRoot) return;
    this.injectSheet(
      form.shadowRoot,
      commentFormOverrideRoots,
      COMMENT_FORM_OVERRIDES,
    );
  }

  private injectSheet(
    root: ShadowRoot,
    seen: WeakSet<ShadowRoot>,
    css: string,
  ): void {
    if (seen.has(root) || typeof CSSStyleSheet === "undefined") return;
    if (!("adoptedStyleSheets" in root)) return;
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      seen.add(root);
    } catch {
      // Best-effort: the form then keeps a button that does nothing, which stays usable.
    }
  }

  /**
   * Keeps the count for the length notice. The form's own send button and invalid state
   * follow from its `characterLimit` check on every `changed`, which counts through
   * useMessageCharacterCount (see handleCommentFormConnected).
   */
  private handleContentChanged(event: Event): void {
    const form = event.currentTarget as CommentFormApi | null;
    this.messageLength = countMessageCharacters(form?.getContent?.() ?? "");
  }

  private isMessageTooLong(): boolean {
    return isMessageTooLong(this.messageLength, this.maxMessageLength);
  }

  /**
   * Bottom edge of the input box, in viewport coordinates.
   *
   * The AFAS comment form is compact until it has focus, when it grows by an action row.
   * Sending takes that focus away, so the box shrinks again -- and it does so from the
   * top, because everything below it keeps its place. That makes this edge the one part
   * of the composer that holds still through the change, and therefore the only sound
   * reference for the thread's slide from the landing screen to the bottom.
   */
  inputBottom(): number {
    return (this.commentForm ?? this).getBoundingClientRect().bottom;
  }

  private focusCommentForm(): void {
    // Defer to the next frame: when the composer re-enables after an answer
    // finishes (#142), afas-comment-form clears its `busy` -> `disabled` one
    // extra update cycle later, so focusing synchronously would hit a
    // still-disabled textarea. Re-check busy/locked in case the state flipped
    // back (e.g. a new question started) before the frame ran.
    requestAnimationFrame(() => {
      if (this.busy || this.locked) return;
      this.commentForm?.focus?.();
    });
  }

  private async handleAttachmentChange(
    event: CustomEvent<{ value?: File; files?: File[] }>,
  ): Promise<void> {
    // afas-file-input dispatches `change` once per selected file, each event
    // carrying that single file in `detail.value` (the full batch is repeated
    // in `detail.files`). Read the per-event `value` so a multi-file selection
    // adds each file exactly once; reading `files` would re-add the whole batch
    // on every event and duplicate the attachments.
    const file = event.detail.value;
    if (!(file instanceof File)) return;
    await this.addAttachmentFiles([file]);
  }

  private async handleAfasSubmit(event: CustomEvent<unknown>): Promise<void> {
    if (this.busy || this.locked) return;
    // Enter reaches the form's own submit even when its send button is disabled, so the
    // length is checked here; the notice above the input says what is wrong.
    if (this.isMessageTooLong()) return;

    const message = getSubmittedMessage(event.detail);
    if (!message) return;

    const attachments = this.attachments;

    const form = (event.currentTarget ?? event.target) as CommentFormApi | null;
    if (typeof form?.reset === "function") form.reset();
    else if (typeof form?.setContent === "function") form.setContent("");
    this.messageLength = 0;
    this.attachments = [];

    this.dispatchEvent(
      new CustomEvent("jonas-question-submit", {
        detail: { message, attachments: attachments },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleStopButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("jonas-chat-stop", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private renderAttachments() {
    if (!this.attachments.length) return null;
    return html`<div class="attachment-list" aria-live="polite">
      <afas-file-group display="grid" .collapsible=${false}>
        ${this.attachments.map((attachment, index) => {
          const afasFile = toAfasFile(attachment, index, "composer");
          const previewUrl = afasFile.previewUrl;
          return html`<afas-file
            tone="neutral"
            .file=${afasFile}
            .viewEnabled=${Boolean(previewUrl)}
            .removeEnabled=${true}
            .replaceEnabled=${false}
            .imageEditEnabled=${false}
            @preview=${this.handleAttachmentPreview}
            @delete=${() => this.removeAttachment(index)}
          ></afas-file>`;
        })}
      </afas-file-group>
    </div>`;
  }

  /**
   * The one way in for picker, paste and drop (#385): a refused file (type, size, no slot
   * left) shows the blocked cue on every route. A repeat is not a refusal.
   */
  private async addAttachmentFiles(
    files: File[],
    options: { namePastedImages?: boolean } = {},
  ): Promise<void> {
    if (!files.length) return;
    if (this.attachments.length >= MAX_ATTACHMENT_COUNT) {
      this.flagAttachmentRejected();
      return;
    }

    const supported = files.filter(isSupportedAttachmentFile);
    if (supported.length < files.length) this.flagAttachmentRejected();
    if (!supported.length) return;

    // Read first, then take the slots: a repeat is only known by its content, and the
    // picker fires one change per file, so parallel calls all saw a free slot up here.
    const incoming = await Promise.all(supported.map(readAttachmentFile));
    const { admitted, overflow } = admitNewAttachments(
      this.attachments,
      incoming,
      MAX_ATTACHMENT_COUNT,
    );
    if (overflow > 0) this.flagAttachmentRejected();
    if (!admitted.length) return;

    const prepared = options.namePastedImages
      ? this.withPastedImageNames(admitted)
      : admitted;
    this.attachments = [...this.attachments, ...prepared];
  }

  private withPastedImageNames(
    attachments: ChatAttachmentInput[],
  ): ChatAttachmentInput[] {
    const baseLabel = this.getLabel(
      "attachmentPastedImage",
      "Geplakte afbeelding",
    );
    const usedDisplayNames = new Set(
      this.attachments.map((attachment) =>
        stripFileExtension(attachment.fileName),
      ),
    );
    return attachments.map((attachment) => {
      const displayName = nextPastedImageName(baseLabel, usedDisplayNames);
      usedDisplayNames.add(displayName);
      return {
        ...attachment,
        fileName: `${displayName}.${imageFileExtension(attachment.contentType)}`,
      };
    });
  }

  private removeAttachment(indexToRemove: number): void {
    this.attachments = this.attachments.filter(
      (_attachment, index) => index !== indexToRemove,
    );
  }

  private handleAttachmentPreview(
    event: CustomEvent<AfasAttachmentFile>,
  ): void {
    const file = event.detail;
    const src = file?.previewUrl;
    if (!src) return;
    // Show the pasted image in the shared app-root dialog (see jonas-image-dialog).
    requestImageDialog(this, {
      src,
      alt: file.filename,
      title: file.filename,
    });
  }

  // The comment-form markdown editor calls preventDefault on every paste and
  // silently drops image data, so intercept image pastes in the capture phase
  // (before the editor) and turn them into attachments. Text pastes fall
  // through to the editor untouched.
  private readonly handleComposerPaste = (event: ClipboardEvent): void => {
    if (!event.composedPath().includes(this)) return;
    if (clipboardHasText(event)) return;
    const files = getClipboardImageFiles(event);
    if (!files.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void this.addAttachmentFiles(files, { namePastedImages: true });
  };

  // Drag-and-drop image attachments (#110). Like the paste handler, these run
  // on window capture scoped to the composer subtree so they intercept the
  // drop before the AFAS markdown editor, which otherwise swallows the file.
  private readonly handleDragEnter = (event: DragEvent): void => {
    if (this.locked || !this.eventTargetsComposer(event)) return;
    if (!this.dragHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    this.dragDepth += 1;
    this.dragState = this.evaluateDrag(event.dataTransfer);
  };

  private readonly handleDragOver = (event: DragEvent): void => {
    if (this.locked || !this.eventTargetsComposer(event)) return;
    if (!this.dragHasFiles(event.dataTransfer)) return;
    // The drop event only fires when dragover calls preventDefault.
    event.preventDefault();
    const state = this.evaluateDrag(event.dataTransfer);
    this.dragState = state;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = state === "valid" ? "copy" : "none";
    }
  };

  private readonly handleDragLeave = (event: DragEvent): void => {
    if (!this.eventTargetsComposer(event)) return;
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this.dragState = "none";
  };

  private readonly handleDrop = (event: DragEvent): void => {
    if (!this.eventTargetsComposer(event)) return;
    const files = this.readDroppedFiles(event.dataTransfer);
    // Only intercept genuine file drops; text/link drops fall through to the editor.
    if (!files.length) {
      this.resetDragState();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.resetDragState();
    if (this.locked) return;
    void this.addAttachmentFiles(files);
  };

  private eventTargetsComposer(event: Event): boolean {
    return event.composedPath().includes(this);
  }

  private dragHasFiles(transfer: DataTransfer | null): boolean {
    if (!transfer) return false;
    return Array.from(transfer.items).some((item) => item.kind === "file");
  }

  private evaluateDrag(transfer: DataTransfer | null): "valid" | "blocked" {
    if (MAX_ATTACHMENT_COUNT - this.attachments.length <= 0) return "blocked";
    if (!transfer) return "valid";
    const fileItems = Array.from(transfer.items).filter(
      (item) => item.kind === "file",
    );
    if (!fileItems.length) return "blocked";
    // A drag only exposes the mime type (not the size), so accept when at least
    // one item is a supported image or when the browser hides the type (empty).
    const anySupported = fileItems.some(
      (item) =>
        !item.type || SUPPORTED_ATTACHMENT_TYPES.has(item.type.toLowerCase()),
    );
    return anySupported ? "valid" : "blocked";
  }

  private readDroppedFiles(transfer: DataTransfer | null): File[] {
    if (!transfer) return [];
    return Array.from(transfer.files).filter((file) => file instanceof File);
  }

  private resetDragState(): void {
    this.dragDepth = 0;
    this.dragState = "none";
  }

  private flagAttachmentRejected(): void {
    this.attachmentRejected = true;
    if (this.attachmentRejectedTimer) clearTimeout(this.attachmentRejectedTimer);
    this.attachmentRejectedTimer = setTimeout(() => {
      this.attachmentRejected = false;
      this.attachmentRejectedTimer = null;
    }, ATTACHMENT_REJECTED_HINT_MS);
  }

  private getLabel(key: string, fallback: string): string {
    const value = this.labels?.[key]?.trim();
    return value || fallback;
  }
}

function getClipboardImageFiles(event: ClipboardEvent): File[] {
  const clipboardData = event.clipboardData;
  if (!clipboardData) return [];

  const files = Array.from(clipboardData.files).filter(
    (file) => file instanceof File,
  );
  if (files.length) return files;

  return Array.from(clipboardData.items)
    .filter((item) => item.kind === "file")
    .map((item) => item.getAsFile())
    .filter((file): file is File => file instanceof File);
}

function clipboardHasText(event: ClipboardEvent): boolean {
  const text = event.clipboardData?.getData("text/plain") ?? "";
  return text.trim().length > 0;
}

function stripFileExtension(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, "");
}

function imageFileExtension(contentType: string): string {
  return contentType.toLowerCase() === "image/png" ? "png" : "jpg";
}

function nextPastedImageName(
  baseLabel: string,
  usedDisplayNames: ReadonlySet<string>,
): string {
  if (!usedDisplayNames.has(baseLabel)) return baseLabel;
  let index = 2;
  while (usedDisplayNames.has(`${baseLabel} ${index}`)) {
    index += 1;
  }
  return `${baseLabel} ${index}`;
}

async function readAttachmentFile(file: File): Promise<ChatAttachmentInput> {
  const buffer = await file.arrayBuffer();
  return {
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    size: file.size,
    data: arrayBufferToBase64(buffer),
  };
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function getSubmittedMessage(detail: unknown): string {
  if (typeof detail === "string") return unescapeMarkdown(detail.trim());
  if (isRecord(detail) && typeof detail.value === "string") {
    return unescapeMarkdown(detail.value.trim());
  }
  return "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-chat-composer": HelpJonasChatComposer;
  }
}
